/**
 * Helpers for distinguishing a Vimeo watch URL from a plain media file URL
 * and turning it into an embeddable player URL.
 *
 * Vimeo watch URLs come in a few shapes:
 *   - public:   https://vimeo.com/1094330527
 *   - unlisted: https://vimeo.com/1094330527/84be7382e4   (second path segment is the privacy hash)
 *   - unlisted: https://vimeo.com/1094330527?h=84be7382e4 (privacy hash as a query param)
 *
 * Anything else (e.g. https://healthcoach-media.com/res/videos/clip.mp4#t=2) is
 * treated as a plain media file to be played by a native <video> element.
 */
const VIMEO_URL_PATTERN = /^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)(?:\/([0-9a-z]+))?/i;
export const isVimeoUrl = (url) => typeof url === "string" && VIMEO_URL_PATTERN.test(url.trim());
export const parseVimeoUrl = (url) => {
    if (typeof url !== "string")
        return null;
    const trimmedUrl = url.trim();
    const match = VIMEO_URL_PATTERN.exec(trimmedUrl);
    if (!match)
        return null;
    const videoId = match[1];
    let privacyHash = match[2] ?? null;
    // Fall back to the `h=` query param form when the hash isn't a path segment.
    if (!privacyHash) {
        const queryHashMatch = /[?&]h=([0-9a-z]+)/i.exec(trimmedUrl);
        if (queryHashMatch)
            privacyHash = queryHashMatch[1];
    }
    return { videoId, privacyHash };
};
export const buildVimeoEmbedUrl = (url) => {
    const parsed = parseVimeoUrl(url);
    if (!parsed)
        return null;
    const params = new URLSearchParams();
    if (parsed.privacyHash)
        params.set("h", parsed.privacyHash);
    const queryString = params.toString();
    return `https://player.vimeo.com/video/${parsed.videoId}${queryString ? `?${queryString}` : ""}`;
};
